import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationApi } from "../api/notifications";
import { requireData, yieldToCancellation } from "./http";
import { isAuthStatus } from "./auth";

export const notificationQueryKeys = {
  all: (userId: string) => ["notifications", userId] as const,
  unread: (userId: string) => ["notifications", userId, "unread"] as const,
  page: (userId: string, cursor?: string) =>
    ["notifications", userId, "page", cursor ?? "first"] as const,
};
const refreshInterval = (error: unknown) =>
  isAuthStatus(error, 401) || isAuthStatus(error, 403) ? false : 30_000;

export function useNotificationCount(userId: string) {
  return useQuery({
    queryKey: notificationQueryKeys.unread(userId),
    queryFn: async ({ signal }) => {
      await yieldToCancellation(signal);
      return requireData(await notificationApi.unreadCount(signal))
        .unread_count;
    },
    enabled: Boolean(userId),
    refetchInterval: (query) => refreshInterval(query.state.error),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 10_000,
  });
}

export function useNotifications(
  userId: string,
  open: boolean,
  cursor?: string,
) {
  return useQuery({
    queryKey: notificationQueryKeys.page(userId, cursor),
    queryFn: async ({ signal }) => {
      await yieldToCancellation(signal);
      return requireData(
        await notificationApi.list({ cursor, limit: 20 }, signal),
      );
    },
    enabled: Boolean(userId) && open,
    refetchInterval: (query) =>
      open ? refreshInterval(query.state.error) : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
  });
}

export function useReadNotification(userId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      requireData(await notificationApi.markRead(id)),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: notificationQueryKeys.all(userId) }),
  });
}

export function useReadAllNotifications(userId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (through: string) =>
      requireData(await notificationApi.markAllRead({ through })),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: notificationQueryKeys.all(userId) }),
  });
}
